import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconPlus } from '~/ui/core/icons';
import { Popover, PopoverContent, PopoverTrigger } from '~/ui/core/popover/Popover';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { Spinner } from '~/ui/core/spinner/Spinner';
import { floatingSurfaceItemClassNames } from '~/ui/core/surface/surfaceClasses';
import { SearchInput } from '~/ui/domain/search-input/SearchInput';

import { useRoleComparisonSearch } from '../api/use-role-comparison-search';
import { useInfiniteScroll } from '../hooks/use-infinite-scroll';
import { uniqueParentIds, useParentTitles } from './use-parent-titles';

const SEARCH_DEBOUNCE_MS = 300;
const LOADING_ROW_IDS = ['first', 'second', 'third'] as const;

const SearchResults: FunctionComponent<{
    readonly architectureId: string;
    readonly excludedItemIds: readonly string[];
    readonly itemType: 'job-level' | 'job-role';
    readonly searchTerm: string;
    readonly onSelect: (itemId: string) => void;
}> = ({ architectureId, excludedItemIds, itemType, onSelect, searchTerm }) => {
    const translate = useTranslate();
    const scrollContainer = useRef<HTMLDivElement>(null);
    const { error, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, results } = useRoleComparisonSearch({
        architectureId,
        enabled: true,
        excludedItemIds,
        itemType,
        searchTerm,
    });
    const parentIds = useMemo(() => uniqueParentIds(results.map((result) => result.parentId)), [results]);
    const parentTitles = useParentTitles(architectureId, itemType, parentIds);
    const { observerRef } = useInfiniteScroll({
        hasNextPage,
        isFetchingNextPage,
        onLoadMore: fetchNextPage,
        root: scrollContainer,
    });

    if (isLoading) {
        return (
            <div className="flex flex-col gap-s px-xs py-xs" aria-busy={true}>
                {LOADING_ROW_IDS.map((rowId) => (
                    <SkeletonBase key={rowId} className="h-xl w-full" />
                ))}
            </div>
        );
    }

    if (!is.null(error)) {
        return (
            <div className="px-s py-s text-body-xs text-danger" role="alert" title={error.message}>
                {translate('pages.job-architecture-v2.job-profile-error-prefix')}
            </div>
        );
    }

    if (is.emptyArray(results)) {
        return (
            <div className="px-s py-s text-body-xs text-text-muted">
                {translate(
                    itemType === 'job-role'
                        ? 'pages.job-architecture-v2.job-profile-role-comparison-no-results'
                        : 'pages.job-architecture-v2.job-profile-level-comparison-no-results',
                )}
            </div>
        );
    }

    return (
        <div ref={scrollContainer} className="flex max-h-[18rem] flex-col overflow-y-auto" role="listbox">
            {results.map((result) => {
                const parentTitle = is.undefined(result.parentId) ? undefined : parentTitles.get(result.parentId);

                return (
                    <button
                        key={result.id}
                        className={`flex w-full flex-col items-start gap-xxs rounded-md border-0 bg-transparent px-s py-xs text-left ${floatingSurfaceItemClassNames.hover}`}
                        data-testid={`role-comparison-search-result-${result.id}`}
                        role="option"
                        type="button"
                        onClick={() => onSelect(result.id)}
                    >
                        <span className="line-clamp-2 text-body-s text-text-primary">{result.title}</span>
                        {is.nonEmptyString(parentTitle) ? (
                            <span className="line-clamp-1 text-body-xxs text-text-muted">{parentTitle}</span>
                        ) : null}
                    </button>
                );
            })}
            {isFetchingNextPage ? (
                <div className="flex justify-center py-xs">
                    <Spinner className="mr-0 text-text-primary" />
                </div>
            ) : null}
            {hasNextPage ? <div ref={observerRef} className="h-px shrink-0" /> : null}
        </div>
    );
};

export const AddRolePopover: FunctionComponent<{
    readonly architectureId: string;
    // Opens the search right away, so a comparison started from one item shows where to pick the next.
    readonly defaultOpen?: boolean;
    readonly excludedItemIds: readonly string[];
    readonly itemType: 'job-level' | 'job-role';
    readonly onAddItem: (itemId: string) => void;
}> = ({ architectureId, defaultOpen = false, excludedItemIds, itemType, onAddItem }) => {
    const translate = useTranslate();
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm] = useDebounceValue(searchTerm, SEARCH_DEBOUNCE_MS);

    const handleOpenChange = (nextOpen: boolean): void => {
        setIsOpen(nextOpen);
        if (!nextOpen) {
            setSearchTerm('');
        }
    };

    return (
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild={true}>
                <Button data-testid="role-comparison-add-role" size="sm" variant="secondary">
                    <IconPlus size={16} />
                    {translate(
                        itemType === 'job-role'
                            ? 'pages.job-architecture-v2.job-profile-role-comparison-add-role'
                            : 'pages.job-architecture-v2.job-profile-level-comparison-add-level',
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="flex w-[20rem] flex-col gap-s" sideOffset={6}>
                <SearchInput
                    autoFocus={true}
                    placeholder={translate(
                        itemType === 'job-role'
                            ? 'pages.job-architecture-v2.job-profile-role-comparison-search-placeholder'
                            : 'pages.job-architecture-v2.job-profile-level-comparison-search-placeholder',
                    )}
                    size="sm"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                />
                {isOpen ? (
                    <SearchResults
                        architectureId={architectureId}
                        excludedItemIds={excludedItemIds}
                        itemType={itemType}
                        searchTerm={debouncedSearchTerm}
                        onSelect={(itemId) => {
                            onAddItem(itemId);
                            handleOpenChange(false);
                        }}
                    />
                ) : null}
            </PopoverContent>
        </Popover>
    );
};
